import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ICompanyDTO, ICompanyNameIdLegalFormDTO } from '@modeso/types__aumico-lib-companies-be';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import Debug from 'debug';
import { Observable } from 'rxjs';
import * as fromCompaniesAction from '../actions/companies.actions';
import { ICompanyExportDto } from '../dtos/company-export-dto.interface';
import { ICompanyExportTableHeaders } from '../dtos/company-export-table-header-dto.interface';
import { CompanyValuationRequestDTO } from '../dtos/company-valuation-request-dto.interface';
import { TasksUpdate } from '../enums/tasks-update.enum';
import { FeatureState } from '../reducer/companies.reducer';
import * as fromFeature from './../reducer/companies.reducer';
import { ICompaniesProvider } from './companies.provider.interface';

const debug = Debug('aumico:aumico-lib-companies-fe:CompaniesProvider');

@Injectable({
    providedIn: 'root',
})
export class CompaniesProvider implements ICompaniesProvider {
    constructor(private store: Store<FeatureState>, private actions$: Actions) {
        debug('provider constructed');
    }

    createCompany(company: ICompanyDTO, logo: File | null, activateFx?: boolean) {
        this.store.dispatch(fromCompaniesAction.createCompany({ company, logo, activateFx }));
    }

    dispatchUpdateCompany(company: ICompanyDTO, logo: File | null, tasksUpdate?: TasksUpdate) {
        this.store.dispatch(fromCompaniesAction.updateCompany({ company, logo, tasksUpdate }));
    }

    dispatchBulkUpdateCompanies(companies: ICompanyDTO[], logo: File | null, tasksUpdate?: TasksUpdate) {
        this.store.dispatch(fromCompaniesAction.bulkUpdateCompanies({ companies, logo, tasksUpdate }));
    }

    dispatchUpdateCompanyStatus(active: boolean, id: string) {
        this.store.dispatch(fromCompaniesAction.changeStatus({ payload: { _id: id, active: active } }));
    }
    dispatchDeleteCompany(id: string) {
        this.store.dispatch(fromCompaniesAction.deleteCompany({ payload: { _id: id } }));
    }

    dispatchCompaniesList(): void {
        this.store.dispatch(fromCompaniesAction.getCompanies());
    }

    dispatchCompaniesNameIdLegalFormList(): void {
        this.store.dispatch(fromCompaniesAction.getCompaniesNameIdLegalForm());
    }

    getCompanies$(): Observable<ICompanyDTO[]> {
        return this.store.pipe(select(fromFeature.selectAllCompanies));
    }

    getCompaniesNameIdLegalForm$(): Observable<ICompanyNameIdLegalFormDTO[]> {
        return this.store.pipe(select(fromFeature.selectCompaniesNameIdLegalForm));
    }

    dispatchCompanyById(id: string): void {
        this.store.dispatch(fromCompaniesAction.getCompanyById({ payload: id }));
    }

    getSelectedCompanyById$(): Observable<ICompanyDTO> {
        return this.store.pipe(select(fromFeature.selectSelectedCompanyById));
    }

    getCompanyCreationResponse$(): Observable<{ company: ICompanyDTO }> {
        return this.store.pipe(select(fromFeature.selectCompanyCreationResponse));
    }

    getCompaniesError$(): Observable<HttpErrorResponse> {
        return this.store.pipe(select(fromFeature.selectCompaniesError));
    }

    getCompaniesResponse$(): Observable<ICompanyDTO> {
        return this.store.pipe(select(fromFeature.selectCompaniesResponse));
    }

    dispatchCompanyValuation(companyValuationRequest: CompanyValuationRequestDTO[]): void {
        this.store.dispatch(fromCompaniesAction.companyValuation({ payload: companyValuationRequest }));
    }

    downloadDefaultCompanyValuationFile(): void {
        this.store.dispatch(fromCompaniesAction.downloadDefaultCompanyValuationFile());
    }

    downloadCompanyLogo(logoUUID: string): void {
        this.store.dispatch(fromCompaniesAction.getCompanyLogo.download({ documentId: logoUUID }));
    }

    resetCompanies() {
        debug('reset companies');
        this.store.dispatch(fromCompaniesAction.resetCompanies());
    }

    resetErrors() {
        debug('reset companies');
        this.store.dispatch(fromCompaniesAction.resetErrors());
    }

    isCompanyUrlIdValid$(id: string): Observable<boolean> {
        return this.store.pipe(select(fromFeature.isCompanyUrlIdValid(id)));
    }

    public dispatchExportCompaniesDataAsExcel(
        tableHeaders: ICompanyExportTableHeaders,
        companies: ICompanyExportDto[],
        fileName: string,
    ): void {
        this.store.dispatch(
            fromCompaniesAction.exportCompaniesDataAsExcel.do({ payload: { tableHeaders, companies, fileName } }),
        );
    }

    /**
     * Returns an observable that emits when a delete company operation completes (success or failure)
     * @returns Observable that emits the action with proper typing for success/failure handling
     */
    getDeleteCompanyResult$(): Observable<
        | { type: typeof fromCompaniesAction.onDeleteCompanySuccessfully.type; payload: ICompanyDTO }
        | { type: typeof fromCompaniesAction.onDeleteCompanyFailed.type; payload: HttpErrorResponse }
    > {
        return this.actions$.pipe(
            ofType(fromCompaniesAction.onDeleteCompanySuccessfully, fromCompaniesAction.onDeleteCompanyFailed),
        );
    }

    /**
     * Returns an observable that emits when a bulk update companies operation completes (success or failure)
     * @returns Observable that emits the action with proper typing for success/failure handling
     */
    getBulkUpdateCompaniesResult$(): Observable<
        | { type: typeof fromCompaniesAction.onBulkUpdateCompaniesSuccessfully.type; payload: ICompanyDTO[] }
        | { type: typeof fromCompaniesAction.onBulkUpdateCompaniesFailed.type; payload: HttpErrorResponse }
    > {
        return this.actions$.pipe(
            ofType(fromCompaniesAction.onBulkUpdateCompaniesSuccessfully, fromCompaniesAction.onBulkUpdateCompaniesFailed),
        );
    }
}
